import React from "react";
import { Reveal, SectionLabel } from "./Reveal";
import { useLanguage } from "@/i18n/LanguageContext";
import { Butterfly } from "./Brand";

// Per-plane accent palette — each plane has its own temperature.
const planeStyles = {
  body: {
    accent: "bg-livon-blush",
    soft: "bg-livon-blush/30",
    dot: "bg-livon-blush",
    numeral: "text-livon-ink/15",
  },
  mind: {
    accent: "bg-livon-lavender",
    soft: "bg-livon-mist/40",
    dot: "bg-livon-lavender",
    numeral: "text-livon-lavender/35",
  },
  soul: {
    accent: "bg-livon-ink",
    soft: "bg-livon-cloud/40",
    dot: "bg-livon-cloud",
    numeral: "text-livon-ink/15",
  },
};

const PlaneRow = ({ plane, areaItems, index, total }) => {
  const s = planeStyles[plane.key];
  const isLast = index === total - 1;

  // Match the plane.areas numerals against the areas data to render rich detail.
  const detailedAreas = plane.areas
    .map((n) => areaItems.find((a) => a.n === n))
    .filter(Boolean);

  return (
    <div
      data-testid={`plane-${plane.key}`}
      className={`relative ${s.soft} border-t border-livon-line/60 ${isLast ? "border-b border-livon-line/60" : ""}`}
    >
      {/* Accent rail on the very left */}
      <div className={`absolute left-0 top-0 bottom-0 w-1.5 md:w-2 ${s.accent}`} aria-hidden="true" />

      <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          {/* Numeral */}
          <div className="lg:col-span-2">
            <Reveal>
              <div
                className={`serif leading-none ${s.numeral}`}
                style={{ fontSize: "clamp(5rem, 10vw, 10rem)", fontWeight: 400 }}
                aria-hidden="true"
              >
                {plane.numeral}
              </div>
            </Reveal>
          </div>

          {/* Title + Swedish translation + lead + body */}
          <div className="lg:col-span-5">
            <Reveal delay={0.05}>
              <h3
                className="serif text-livon-ink leading-[0.92] tracking-tight"
                style={{ fontSize: "clamp(3rem, 6vw, 5.5rem)", fontWeight: 500 }}
                data-testid={`plane-${plane.key}-title`}
              >
                {plane.title}
              </h3>
            </Reveal>
            <Reveal delay={0.1}>
              <div
                className="mt-4 text-[11px] tracking-[0.4em] uppercase text-livon-muted font-medium"
                data-testid={`plane-${plane.key}-sv`}
              >
                {plane.sv}
              </div>
            </Reveal>
            <Reveal delay={0.14}>
              <p
                className="mt-8 serif italic text-livon-ink leading-[1.25]"
                style={{ fontSize: "clamp(1.35rem, 1.8vw, 1.65rem)", fontWeight: 400 }}
                data-testid={`plane-${plane.key}-lead`}
              >
                {plane.lead}
              </p>
            </Reveal>
            <Reveal delay={0.18}>
              <p
                className="mt-5 text-livon-muted text-base md:text-lg leading-relaxed max-w-md"
                data-testid={`plane-${plane.key}-body`}
              >
                {plane.body}
              </p>
            </Reveal>
          </div>

          {/* Two life areas belonging to this plane — full detail, not just chips */}
          <div className="lg:col-span-5 lg:pt-6">
            <Reveal delay={0.22}>
              <div className="text-[11px] tracking-[0.3em] uppercase text-livon-muted font-medium mb-6 flex items-center gap-2">
                <span className={`inline-block w-1.5 h-1.5 rounded-full ${s.dot}`} />
                {plane.areasLabel}
              </div>
            </Reveal>
            <div className="space-y-6">
              {detailedAreas.map((area, i) => (
                <Reveal key={area.n} delay={0.26 + i * 0.05}>
                  <div
                    data-testid={`plane-${plane.key}-area-${area.n}`}
                    className="flex gap-5 md:gap-6"
                  >
                    <div
                      className={`serif leading-none ${s.numeral} shrink-0`}
                      style={{ fontSize: "clamp(2rem, 3vw, 3rem)", fontWeight: 400 }}
                    >
                      {area.n}
                    </div>
                    <div className="flex-1 pt-1">
                      <h4
                        className="serif text-livon-ink leading-tight"
                        style={{ fontSize: "clamp(1.25rem, 1.7vw, 1.5rem)", fontWeight: 500 }}
                      >
                        {area.t}
                      </h4>
                      <p className="mt-2 text-livon-muted text-sm md:text-[15px] leading-relaxed">
                        {area.d}
                      </p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const ThreePlanes = () => {
  const { t } = useLanguage();
  const p = t.planes;
  const areaItems = t.areas.items;

  return (
    <section
      id="planes"
      data-testid="three-planes-section"
      className="relative bg-livon-bg"
    >
      {/* Section header */}
      <div className="max-w-7xl mx-auto px-6 md:px-10 pt-28 md:pt-40 pb-14 md:pb-20">
        <div className="max-w-4xl">
          <Reveal>
            <SectionLabel testId="planes-label">{p.label}</SectionLabel>
          </Reveal>
          <Reveal delay={0.05}>
            <h2
              className="serif text-livon-ink leading-[0.96] tracking-tight"
              style={{ fontSize: "clamp(3rem, 7vw, 6.5rem)", fontWeight: 500 }}
              data-testid="planes-title"
            >
              {p.title}
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p
              className="mt-8 text-livon-muted text-base md:text-lg leading-relaxed max-w-2xl"
              data-testid="planes-intro"
            >
              {p.intro}
            </p>
          </Reveal>
        </div>
      </div>

      {/* Three editorial rows */}
      <div>
        {p.items.map((plane, i) => (
          <PlaneRow
            key={plane.key}
            plane={plane}
            areaItems={areaItems}
            index={i}
            total={p.items.length}
          />
        ))}
      </div>

      {/* Closing line */}
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28 flex flex-col items-center text-center">
        <Reveal>
          <div className="mb-8 opacity-80">
            <Butterfly size={36} />
          </div>
        </Reveal>
        <Reveal delay={0.06}>
          <p
            data-testid="planes-closing"
            className="serif italic text-livon-ink leading-[1.15]"
            style={{ fontSize: "clamp(1.75rem, 3vw, 2.75rem)", fontWeight: 400 }}
          >
            {p.closing}
          </p>
        </Reveal>
      </div>
    </section>
  );
};
